import { useCallback, useEffect, useRef, useState } from 'react';
import { findTourElement, isMobileViewport, scrollTourTargetIntoView, showAdminTableFullRow, clearAdminTableTourLayout, closeTopbarClientMenu, lockTopbarNavMenu, unlockTopbarNavMenu, applyTourBottomLift, clearTourBottomLift, getHotBarOverlap } from './tourElementUtils';

const AUTO_OPEN_DELAY_MS = 2000;
const PROGRESS_KEY = 'portfolioPlaybookProgress';
const DEFAULT_VOLUME = 0.75;

// Steps that keep a top-bar nav dropdown locked open, click-driven.
const NAV_MENU_STEPS = new Set([
  'grow-company',
]);

function resolveStepTarget(step, isMobile) {
  if (!step?.target) return null;
  const primary = (isMobile && step.targetMobile) ? step.targetMobile : step.target;
  if (findTourElement(primary, { isMobile })) return primary;
  if (step.targetFallback && findTourElement(step.targetFallback, { isMobile })) {
    return step.targetFallback;
  }
  return primary;
}

/** Clickable control may differ from the visual spotlight (e.g. whole menu vs one item). */
function resolveStepClickTarget(step, isMobile) {
  if (step?.clickTarget) {
    const clickId = (isMobile && step.clickTargetMobile) ? step.clickTargetMobile : step.clickTarget;
    if (findTourElement(clickId, { isMobile })) return clickId;
    return clickId;
  }
  return resolveStepTarget(step, isMobile);
}

/** Index of the next chapter card, or the finale step if this is the last section. */
function findSkipSectionIndex(steps, currentStep) {
  if (!Array.isArray(steps) || currentStep < 0) return -1;
  for (let i = currentStep + 1; i < steps.length; i += 1) {
    if (steps[i]?.sectionCard) return i;
  }
  const last = steps.length - 1;
  if (currentStep < last) return last;
  return -1;
}

export function usePortfolioPlaybook({ enabled, autoOpenEligible, completionKey, steps }) {
  const readProgress = () => {
    try {
      if (localStorage.getItem(completionKey) === 'true') return null;
      const raw = sessionStorage.getItem(PROGRESS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && parsed.open ? parsed : null;
    } catch {
      return null;
    }
  };

  const resumed = enabled ? readProgress() : null;
  const clampStep = useCallback((index, list = steps) => {
    if (!Array.isArray(list) || list.length === 0) return 0;
    return Math.min(Math.max(0, index || 0), list.length - 1);
  }, [steps]);

  const [isOpen, setIsOpen] = useState(!!resumed);
  const [showWelcome, setShowWelcome] = useState(resumed ? !!resumed.welcome : true);
  // 'gate' = text-only unlock screen (no video); 'video' = mascot welcome after Start.
  const [welcomeStage, setWelcomeStage] = useState(() => {
    if (!resumed?.welcome) return 'gate';
    return resumed.welcomeStage === 'video' ? 'video' : 'gate';
  });
  const [currentStep, setCurrentStep] = useState(() => {
    if (!resumed) return 0;
    if (!Array.isArray(steps) || steps.length === 0) return resumed.step || 0;
    return Math.min(Math.max(0, resumed.step || 0), steps.length - 1);
  });
  const [highlightedElement, setHighlightedElement] = useState(null);
  const [secondaryHighlightedElement, setSecondaryHighlightedElement] = useState(null);
  const [targetMissing, setTargetMissing] = useState(false);
  const [tooltipKey, setTooltipKey] = useState(0);
  const [volume, setVolume] = useState(DEFAULT_VOLUME);

  const autoOpenTimer = useRef(null);
  const findTimer = useRef(null);

  useEffect(() => {
    if (!steps.length) return;
    setCurrentStep((prev) => clampStep(prev, steps));
  }, [steps, clampStep]);

  const clearProgress = useCallback(() => {
    try {
      sessionStorage.removeItem(PROGRESS_KEY);
    } catch {
      // ignore
    }
  }, []);

  const openTour = useCallback(() => {
    setShowWelcome(true);
    setWelcomeStage('gate');
    setCurrentStep(0);
    setHighlightedElement(null);
    setTargetMissing(false);
    setVolume(DEFAULT_VOLUME);
    setIsOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsOpen(false);
    setHighlightedElement(null);
    setTargetMissing(false);
    closeTopbarClientMenu();
    clearTourBottomLift();
    window.dispatchEvent(new Event('portfolio-playbook-close-referral'));
    window.dispatchEvent(new Event('portfolio-playbook-close-pricing-drawer'));
    window.dispatchEvent(new Event('portfolio-playbook-close-food-drawer'));
    window.dispatchEvent(new Event('portfolio-playbook-close-peek'));
    clearProgress();
  }, [clearProgress]);

  const finishTour = useCallback(() => {
    try {
      localStorage.setItem(completionKey, 'true');
    } catch {
      // ignore
    }
    closeTour();
  }, [completionKey, closeTour]);

  useEffect(() => {
    if (autoOpenTimer.current) {
      clearTimeout(autoOpenTimer.current);
      autoOpenTimer.current = null;
    }
    if (!autoOpenEligible || isOpen) return undefined;
    let alreadyCompleted = false;
    try {
      alreadyCompleted = localStorage.getItem(completionKey) === 'true';
    } catch {
      alreadyCompleted = false;
    }
    if (alreadyCompleted) return undefined;

    autoOpenTimer.current = setTimeout(() => {
      openTour();
      autoOpenTimer.current = null;
    }, AUTO_OPEN_DELAY_MS);

    return () => {
      if (autoOpenTimer.current) {
        clearTimeout(autoOpenTimer.current);
        autoOpenTimer.current = null;
      }
    };
  }, [autoOpenEligible, isOpen, completionKey, openTour]);

  useEffect(() => {
    if (!isOpen) return;
    try {
      sessionStorage.setItem(
        PROGRESS_KEY,
        JSON.stringify({
          open: true,
          step: currentStep,
          welcome: showWelcome,
          welcomeStage: showWelcome ? welcomeStage : null,
        })
      );
    } catch {
      // ignore
    }
  }, [isOpen, currentStep, showWelcome, welcomeStage]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'P' || e.key === 'p')) {
        e.preventDefault();
        try {
          localStorage.removeItem(completionKey);
          localStorage.removeItem('portfolioPlaybookCompleted');
        } catch {
          // ignore
        }
        clearProgress();
        openTour();
        return;
      }
      if (isOpen && e.key === 'Escape') {
        e.preventDefault();
        finishTour();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [completionKey, clearProgress, openTour, isOpen, finishTour]);

  useEffect(() => {
    const handleReplay = (e) => {
      if (!e.detail?.shiftKey) {
        try {
          localStorage.removeItem(completionKey);
          localStorage.removeItem('portfolioPlaybookCompleted');
        } catch {
          // ignore
        }
      }
      clearProgress();
      openTour();
    };
    window.addEventListener('portfolio-playbook-reopen', handleReplay);
    return () => window.removeEventListener('portfolio-playbook-reopen', handleReplay);
  }, [completionKey, clearProgress, openTour]);

  // Scroll/resize ring tracking lives in TourOverlay (imperative DOM updates).
  // Bumping React state here made the glow lag one+ frames behind the table.

  useEffect(() => {
    if (!isOpen || showWelcome) return undefined;
    const step = steps[currentStep];
    if (typeof step?.prepare === 'function') {
      const delay =
        ['client-profile-expanded', 'plan-summary', 'plan-meal-swap', 'plan-meal-alts', 'plan-meal-expanded', 'plan-meal-alternatives'].includes(step.id)
          ? 320
          : 80;
      const timer = setTimeout(() => step.prepare(), delay);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, showWelcome, currentStep, steps]);

  // Close the top-bar client dropdown whenever the tour moves on.
  useEffect(() => {
    if (!isOpen || showWelcome) return;
    closeTopbarClientMenu();
  }, [isOpen, showWelcome, currentStep, steps]);

  // Plans nav steps: lock the dropdown open (click-driven, stays open) while on
  // them; unlock (and close) once the tour moves on.
  useEffect(() => {
    if (!isOpen || showWelcome) {
      unlockTopbarNavMenu();
      return;
    }
    const step = steps[currentStep];
    if (NAV_MENU_STEPS.has(step?.id)) {
      lockTopbarNavMenu();
    } else {
      unlockTopbarNavMenu();
    }
  }, [isOpen, showWelcome, currentStep, steps]);

  // Table intel: fit the full row. Open-client keeps normal columns so the eye stays usable.
  useEffect(() => {
    if (!isOpen || showWelcome) {
      clearAdminTableTourLayout();
      return undefined;
    }
    const stepId = steps[currentStep]?.id;
    if (stepId === 'scan-table-intel') {
      showAdminTableFullRow();
    } else {
      clearAdminTableTourLayout();
    }
    return () => clearAdminTableTourLayout();
  }, [isOpen, showWelcome, currentStep, steps]);

  useEffect(() => {
    if (findTimer.current) {
      clearTimeout(findTimer.current);
      findTimer.current = null;
    }
    if (!isOpen || showWelcome) {
      setHighlightedElement(null);
      setSecondaryHighlightedElement(null);
      setTargetMissing(false);
      return undefined;
    }
    const step = steps[currentStep];
    if (!step || step.mode === 'story' || step.mode === 'transition' || !step.target) {
      setHighlightedElement(null);
      setSecondaryHighlightedElement(null);
      setTargetMissing(false);
      return undefined;
    }

    const isMobile = isMobileViewport();
    const targetId = resolveStepTarget(step, isMobile);
    let attempts = 0;
    const maxAttempts = 45;

    const tryFind = () => {
      const element = findTourElement(targetId, { isMobile });
      if (element) {
        setTargetMissing(false);
        const scrollOpts = { behavior: 'smooth', block: 'center', inline: 'nearest' };
        if (targetId === 'playbook-table-intel' && element.tagName === 'TR') {
          scrollOpts.block = 'nearest';
          showAdminTableFullRow();
        }
        if (step.id === 'act-actions') {
          clearAdminTableTourLayout();
          const tableScroll = document.querySelector('[data-tour-scroll="playbook-admin-table"]');
          if (tableScroll && element) {
            const sRect = tableScroll.getBoundingClientRect();
            const bRect = element.getBoundingClientRect();
            const delta = ((bRect.left + bRect.right) / 2) - ((sRect.left + sRect.right) / 2);
            tableScroll.scrollLeft += delta;
          }
          element.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
          scrollTourTargetIntoView(element, { panelEdge: 'top', panelSize: 210 });
        } else if (step.id === 'client-quick-actions' || step.id === 'to-chat') {
          scrollTourTargetIntoView(element, { panelEdge: 'top', panelSize: 230 });
        } else if (step.id === 'chat-thread') {
          element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        } else if (step.id === 'client-profile' || step.id === 'client-profile-expanded' || step.id === 'plan-meal-expanded') {
          element.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
        } else {
          element.scrollIntoView(scrollOpts);
        }
        const commitHighlight = () => {
          setHighlightedElement(element);
          let secondary = null;
          if (step.secondaryTarget) {
            secondary = findTourElement(step.secondaryTarget, { isMobile });
          }
          setSecondaryHighlightedElement(secondary);
          setTooltipKey((k) => k + 1);
        };

        const SETTLE_INTERVAL = 90;
        const MAX_SETTLE_ATTEMPTS = 16;
        let prevRect = element.getBoundingClientRect();
        let stableCount = 0;
        let settleAttempts = 0;
        const waitForSettle = () => {
          const rect = element.getBoundingClientRect();
          const settled =
            Math.abs(rect.top - prevRect.top) < 1 &&
            Math.abs(rect.left - prevRect.left) < 1 &&
            Math.abs(rect.width - prevRect.width) < 1 &&
            Math.abs(rect.height - prevRect.height) < 1;
          prevRect = rect;
          settleAttempts += 1;
          if ((settled && stableCount >= 1) || settleAttempts >= MAX_SETTLE_ATTEMPTS) {
            commitHighlight();
            return;
          }
          stableCount = settled ? stableCount + 1 : 0;
          findTimer.current = setTimeout(waitForSettle, SETTLE_INTERVAL);
        };
        findTimer.current = setTimeout(waitForSettle, SETTLE_INTERVAL);
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        findTimer.current = setTimeout(tryFind, 150);
      } else {
        setHighlightedElement(null);
        setSecondaryHighlightedElement(null);
        setTargetMissing(true);
        setTooltipKey((k) => k + 1);
      }
    };

    setHighlightedElement(null);
    setSecondaryHighlightedElement(null);
    setTargetMissing(false);
    findTimer.current = setTimeout(tryFind, 120);

    return () => {
      if (findTimer.current) {
        clearTimeout(findTimer.current);
        findTimer.current = null;
      }
    };
  }, [isOpen, showWelcome, currentStep, steps]);

  const startTour = useCallback(() => {
    setVolume(DEFAULT_VOLUME);
    setShowWelcome(false);
    setWelcomeStage('gate');
    setCurrentStep(0);
  }, []);

  // Gate (no video) → video welcome on first click (unlocks browser audio).
  // Video welcome → real tour steps.
  const continueWelcome = useCallback(() => {
    if (welcomeStage === 'gate') {
      setVolume(DEFAULT_VOLUME);
      setWelcomeStage('video');
      return;
    }
    startTour();
  }, [welcomeStage, startTour]);

  const setMascotVolume = useCallback((next) => {
    setVolume(Math.min(1, Math.max(0, next)));
  }, []);

  const goToStepId = useCallback(
    (stepId) => {
      const idx = steps.findIndex((s) => s.id === stepId);
      if (idx >= 0) setCurrentStep(idx);
    },
    [steps]
  );

  const goNext = useCallback(() => {
    setCurrentStep((prev) => {
      if (prev >= steps.length - 1) {
        finishTour();
        return prev;
      }
      return prev + 1;
    });
  }, [steps.length, finishTour]);

  const goPrev = useCallback(() => {
    setCurrentStep((prev) => {
      if (prev <= 0) {
        setShowWelcome(true);
        return 0;
      }
      return prev - 1;
    });
  }, []);

  const canSkipSection = !showWelcome && findSkipSectionIndex(steps, currentStep) >= 0;

  const skipSection = useCallback(() => {
    const nextIdx = findSkipSectionIndex(steps, currentStep);
    // Modals/drawers can block the next chapter — clear them when jumping ahead.
    window.dispatchEvent(new Event('portfolio-playbook-close-referral'));
    window.dispatchEvent(new Event('portfolio-playbook-close-pricing-drawer'));
    window.dispatchEvent(new Event('portfolio-playbook-close-food-drawer'));
    window.dispatchEvent(new Event('portfolio-playbook-close-peek'));
    if (nextIdx < 0) {
      finishTour();
      return;
    }
    setHighlightedElement(null);
    setSecondaryHighlightedElement(null);
    setCurrentStep(nextIdx);
  }, [steps, currentStep, finishTour]);

  // Tag interactive controls only. Informational spotlights (KPI / table row)
  // use the overlay ring — applying this attr there caused hover-zoom on big targets.
  // Wide bars (Personal Details accordion) get a frame glow without scale.
  // Skip waitForEvent / scrollable menus — mutating position there jumps the
  // absolute client dropdown and blocks its inner scroll.
  // When clickTarget ≠ highlight target, tag the clickable control (not the menu shell).
  useEffect(() => {
    if (!highlightedElement) return undefined;
    const step = steps[currentStep];
    if (!(step?.interactive === true)) return undefined;
    if (step?.waitForEvent || step?.scrollFriendly) return undefined;
    const isMobile = isMobileViewport();
    const clickId = resolveStepClickTarget(step, isMobile);
    const el = (clickId && findTourElement(clickId, { isMobile })) || highlightedElement;
    const rect = el.getBoundingClientRect();
    const isWideBar = rect.width > Math.min(280, window.innerWidth * 0.45);
    el.setAttribute('data-playbook-highlight', isWideBar ? 'bar' : 'true');
    el.classList.add('playbook-tour-target');
    return () => {
      el.removeAttribute('data-playbook-highlight');
      el.classList.remove('playbook-tour-target');
    };
  }, [highlightedElement, currentStep, steps]);

  useEffect(() => {
    if (!secondaryHighlightedElement) return undefined;
    const el = secondaryHighlightedElement;
    el.classList.add('playbook-tour-secondary');
    return () => {
      el.classList.remove('playbook-tour-secondary');
    };
  }, [secondaryHighlightedElement]);

  // When the spotlight sits in the hot-bar zone (chat compose), lift page chrome
  // so the control clears Back/Next — then nudge the ring to remeasure.
  useEffect(() => {
    if (!isOpen || showWelcome || !highlightedElement) {
      clearTourBottomLift();
      return undefined;
    }
    const rect = highlightedElement.getBoundingClientRect();
    const overlap = getHotBarOverlap(rect);
    if (overlap <= 0) {
      clearTourBottomLift();
      return undefined;
    }
    applyTourBottomLift(overlap + 16);
    const raf = window.requestAnimationFrame(() => {
      setTooltipKey((k) => k + 1);
    });
    return () => {
      window.cancelAnimationFrame(raf);
      clearTourBottomLift();
    };
  }, [isOpen, showWelcome, highlightedElement, currentStep]);

  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;

  // If the dashboard remounts the target mid-step, re-bind the highlight to the live node.
  useEffect(() => {
    if (!isOpen || showWelcome || !highlightedElement) return undefined;
    const step = steps[currentStep];
    if (!step?.interactive || !step?.target) return undefined;

    const timer = window.setInterval(() => {
      if (highlightedElement.isConnected) return;
      const isMobile = isMobileViewport();
      const targetId = resolveStepTarget(step, isMobile);
      const next = findTourElement(targetId, { isMobile });
      if (next) setHighlightedElement(next);
    }, 350);

    return () => window.clearInterval(timer);
  }, [isOpen, showWelcome, currentStep, steps, highlightedElement]);

  // Capture-phase controller for every spotlight step.
  // Interactive: only the intended target is live (advance / native action).
  // Brief/read-only: every page click is inert — nudge "nothing to click here"
  // (covers spotlight holes + scrollFriendly pass-through, e.g. a disabled action).
  useEffect(() => {
    if (!isOpen || showWelcome) return undefined;
    const step = steps[currentStep];
    if (step?.mode !== 'spotlight') return undefined;

    const isInteractive = step?.interactive === true;

    // Wait for async UI work (e.g. an API call) before advancing.
    if (isInteractive && step?.waitForEvent) {
      let advanced = false;
      let advanceTimer = null;
      const onDone = () => {
        const active = steps[currentStepRef.current];
        if (active?.id !== step.id) return;
        if (advanced) return;
        advanced = true;
        const delay = step.advanceDelayMs ?? 500;
        advanceTimer = window.setTimeout(() => {
          if (steps[currentStepRef.current]?.id !== step.id) return;
          if (step.advanceToStepId) goToStepId(step.advanceToStepId);
          else goNext();
        }, delay);
      };
      window.addEventListener(step.waitForEvent, onDone);
      return () => {
        window.removeEventListener(step.waitForEvent, onDone);
        if (advanceTimer) clearTimeout(advanceTimer);
      };
    }

    const isMobile = isMobileViewport();
    const targetId = isInteractive ? resolveStepClickTarget(step, isMobile) : null;

    let advanced = false;
    let advanceTimer = null;
    const advance = () => {
      if (advanced) return;
      advanced = true;
      const delay = step?.advanceDelayMs ?? (step?.followNativeAction ? 300 : 400);
      advanceTimer = window.setTimeout(() => {
        if (steps[currentStepRef.current]?.id !== step.id) return;
        if (step?.advanceToStepId) {
          goToStepId(step.advanceToStepId);
        } else {
          goNext();
        }
      }, delay);
    };

    const blockAndNudge = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      window.dispatchEvent(new CustomEvent('portfolio-playbook-misclick'));
    };

    const onCapture = (e) => {
      if (e.target?.closest?.('[data-tour-overlay]')) return;

      if (!isInteractive || !targetId) {
        blockAndNudge(e);
        return;
      }

      const hit = e.target?.closest?.(`[data-tour="${targetId}"]`);
      if (hit) {
        if (step?.advanceOnly) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
        advance();
        return;
      }

      blockAndNudge(e);
    };

    document.addEventListener('click', onCapture, true);
    return () => {
      document.removeEventListener('click', onCapture, true);
      if (advanceTimer) clearTimeout(advanceTimer);
    };
  }, [isOpen, showWelcome, currentStep, steps, goNext, goToStepId]);

  return {
    isOpen,
    showWelcome,
    welcomeStage,
    currentStep,
    highlightedElement,
    secondaryHighlightedElement,
    targetMissing,
    tooltipKey,
    volume,
    setMascotVolume,
    startTour,
    continueWelcome,
    skipWelcome: finishTour,
    goNext,
    goPrev,
    goToStepId,
    skipSection,
    canSkipSection,
    skipTour: finishTour,
  };
}
